/**
 * OnboardingPlans — SaaS-style welcome + plan comparison for freshly-
 * signed-up employers. Renders when a company has ``onboarding_status
 * === "plans_pending"``. Shows the plan matrix from GET /api/plans with
 * two calls to action: "Continue on Free" (marks onboarding complete +
 * lands on /dashboard) or "Upgrade" (kicks off Cashfree checkout for the
 * selected paid plan).
 *
 * Skippable — new users can always come back via /billing later.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Sparkles, ArrowRight } from "lucide-react";

export default function OnboardingPlans() {
    const nav = useNavigate();
    const { user, refresh } = useAuth() || {};
    const [plans, setPlans] = useState([]);
    const [assignedPlan, setAssignedPlan] = useState("free");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: p }, { data: sub }] = await Promise.all([
                    api.get("/plans"),
                    api.get("/subscription").catch(() => ({ data: {} })),
                ]);
                setPlans(p?.plans || []);
                setAssignedPlan(sub?.subscription?.plan_key || "free");
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    async function continueFree() {
        setBusy("free");
        try {
            await api.post("/onboarding/complete");
            toast.success("Welcome aboard! You can upgrade any time from Billing.");
            await refresh?.();
            nav("/dashboard");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    }

    async function upgradeTo(planKey) {
        setBusy(planKey);
        try {
            const { data } = await api.post("/subscription/checkout", { plan_key: planKey, currency: "INR" });
            // Best-effort: mark onboarding complete immediately so if they
            // abandon the checkout they don't get stuck on this page.
            try { await api.post("/onboarding/complete"); } catch { /* silent */ }
            if (data.integration === "cashfree" && data.payment_session_id) {
                // Redirect to Cashfree via their hosted-checkout URL scheme.
                const base = data.env === "PROD" ? "https://payments.cashfree.com" : "https://payments-test.cashfree.com";
                window.location.href = `${base}/pgapp/pgv/redirect?sessionId=${data.payment_session_id}`;
                return;
            }
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
                return;
            }
            toast.success("Checkout started — check your email for the payment link.");
            nav("/billing");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    }

    if (loading) {
        return (
            <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-ikb" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50 py-8 px-4" data-testid="onboarding-plans">
            <div className="max-w-6xl mx-auto">
                <div className="text-center mb-8">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ikb">Welcome, {user?.name?.split(" ")[0]}</div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">Choose the plan that fits your team</h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        You're on the <b className="uppercase font-mono text-ikb">{assignedPlan}</b> plan by default. Upgrade any time to unlock more seats, jobs, and AI-driven features.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {plans.map((p) => {
                        const isCurrent = p.key === assignedPlan;
                        const isPopular = p.popular;
                        return (
                            <div
                                key={p.key}
                                className={`bg-white border p-5 relative ${isPopular ? "border-ikb border-2" : "border-border"}`}
                                data-testid={`onboarding-plan-${p.key}`}
                            >
                                {isPopular && (
                                    <div className="absolute -top-3 left-4 bg-ikb text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5">
                                        Most popular
                                    </div>
                                )}
                                {isCurrent && (
                                    <div className="absolute -top-3 right-4 bg-emerald-600 text-white text-[10px] font-mono uppercase tracking-widest px-2 py-0.5">
                                        Your current
                                    </div>
                                )}
                                <div className="font-display font-black text-xl">{p.name}</div>
                                <div className="mt-2">
                                    <span className="font-display font-black text-3xl tabular-nums">₹{p.price_inr?.toLocaleString("en-IN") || 0}</span>
                                    <span className="text-xs text-muted-foreground ml-1">/mo</span>
                                </div>
                                <ul className="mt-4 space-y-1.5 text-xs">
                                    {(p.features || []).map((f, i) => (
                                        <li key={i} className="flex items-start gap-1.5">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-5">
                                    {isCurrent ? (
                                        <button
                                            type="button"
                                            onClick={continueFree}
                                            disabled={busy === "free"}
                                            className="w-full h-10 bg-neutral-900 text-white text-xs font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60"
                                            data-testid={`onboarding-continue-${p.key}`}
                                        >
                                            {busy === "free" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                                            Continue on {p.name}
                                        </button>
                                    ) : p.key === "free" ? (
                                        <button type="button" disabled className="w-full h-10 border border-border text-muted-foreground text-xs font-mono uppercase tracking-widest">
                                            —
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => upgradeTo(p.key)}
                                            disabled={busy === p.key}
                                            className={`w-full h-10 text-xs font-mono uppercase tracking-widest inline-flex items-center justify-center gap-1.5 disabled:opacity-60 ${isPopular ? "bg-ikb text-white hover:bg-ikb/90" : "border border-ikb text-ikb hover:bg-ikb/5"}`}
                                            data-testid={`onboarding-upgrade-${p.key}`}
                                        >
                                            {busy === p.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                                            Upgrade to {p.name}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="text-center mt-8">
                    <button
                        type="button"
                        onClick={continueFree}
                        disabled={busy === "free"}
                        className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground underline underline-offset-4"
                        data-testid="onboarding-skip"
                    >
                        Skip for now — decide later
                    </button>
                    <div className="text-[11px] text-muted-foreground mt-2">
                        You can change your plan any time from <a href="/billing" className="text-ikb underline">Billing settings</a>.
                    </div>
                </div>
            </div>
        </div>
    );
}
